import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  Configuration,
  EventMessage,
  PublicClientApplication,
} from '@azure/msal-browser';
import type { AccountFilter } from '@azure/msal-common';
import { useEffect, useMemo } from 'react';
import { DefaultEventHandler, handleEvents, type EventHandlers } from '../utils/handleEvents';
import { logoutOnAuthError } from '../utils/logoutOnAuthError';

export function useAuthClient({
  authConfig,
  accountFilter,
  enableAccountStorageEvents,
  eventHandlers,
}: {
  authConfig: Configuration;
  accountFilter?: AccountFilter;
  enableAccountStorageEvents?: boolean;
  eventHandlers: EventHandlers<DefaultEventHandler>;
}) {
  const client = useMemo(() => new PublicClientApplication(authConfig), [authConfig, eventHandlers]);

  useEffect(() => {
    client.initialize().then(async () => {
      const setActiveAccount = (account: AccountInfo | null) => client.setActiveAccount(account);
      const activeAccount = client.getActiveAccount();
      try {
        if (!activeAccount) {
          // Account selection logic is app dependent. Adjust as needed for different use cases.
          const allAccounts = accountFilter ? client.getAllAccounts() : client.getAllAccounts();

          if (allAccounts.length > 0) {
            // Default to using the first account if no account is active on page load
            setActiveAccount(allAccounts[0]);
          } else {
            console.log('No accounts detected.');
          }
        } else {
          console.log('Active account already set');
        }

        // Optional - This will update account state if a user signs in from another tab or window
        enableAccountStorageEvents && client.enableAccountStorageEvents();

        client.addEventCallback(async (event: EventMessage) => {
          const eventType = event.eventType;
          const payload = event.payload;
          await handleEvents(eventType, {
            LOGIN_SUCCESS: () => {
              setActiveAccount((payload as AuthenticationResult).account);
              eventHandlers.LOGIN_SUCCESS?.(eventType, payload);
            },
            ACQUIRE_TOKEN_SUCCESS: () => {
              setActiveAccount((payload as AuthenticationResult).account);
              eventHandlers.ACQUIRE_TOKEN_SUCCESS?.(eventType, payload);
            },
            SSO_SILENT_SUCCESS: () => {
              setActiveAccount((payload as AuthenticationResult).account);
              eventHandlers.SSO_SILENT_SUCCESS?.(eventType, payload);
            },
            ACQUIRE_TOKEN_FAILURE: () => {
              logoutOnAuthError(client, event.error);
              eventHandlers.ACQUIRE_TOKEN_FAILURE?.(eventType, payload);
            },
            ACQUIRE_TOKEN_BY_CODE_FAILURE: () => {
              logoutOnAuthError(client, event.error);
              eventHandlers.ACQUIRE_TOKEN_BY_CODE_FAILURE?.(eventType, payload);
            },
            SSO_SILENT_FAILURE: () => {
              logoutOnAuthError(client, event.error);
              eventHandlers.SSO_SILENT_FAILURE?.(eventType, payload);
            },
            LOGIN_FAILURE: async () => {
              if (event.error instanceof BrowserAuthError) {
                await client.loginRedirect();
              } else {
                await client.logoutRedirect();
              }
              eventHandlers.LOGIN_FAILURE?.(eventType, payload);
            },
            DEFAULT: () => {
              console.log('Unhandled event type: ' + eventType);
              eventHandlers.DEFAULT(eventType, payload);
            },
          });
        });
      } catch (error) {
        console.log('Unhandled event type: ' + error);
      }
    });
  }, [client]);

  return client;
}
